import { setup, assign } from 'xstate';
import { HttpMachine } from 'infrastructure/network/HttpMachine';
import { PrincipalsCollection, Principal, IPrincipal } from "areas/account/models/Principal";
import { closeLoadingScreen } from 'areas/layout/utilities/closeLoadingScreen';
import { UserAccess } from 'areas/users/models/userAccess';
import { datadogLogs } from '@datadog/browser-logs';

const MachineId = 'PrincipalProviderMachine';

interface Context {
    principals?: PrincipalsCollection,
    errorMessage?: string
}

export enum PrincipalProviderMachineStates {
    Fetching = 'Fetching',
    Fetched = 'Fetched',
    FetchError = 'FetchError',
}

export enum PrincipalProviderMachineEvents {
    Refetch = 'Refetch',
}
type EventType = { type: PrincipalProviderMachineEvents.Refetch };

const fetchPrincipalUrl = "/api/v1/account/auth/principal";

export const PrincipalProviderMachine = setup({
    types: {} as {
        context: Context;
        events: EventType;
    },
    actors: {
        httpMachine: HttpMachine,
    },
}).createMachine({
    id: MachineId,
    initial: PrincipalProviderMachineStates.Fetching,
    states: {
        [PrincipalProviderMachineStates.Fetching]: {
            invoke: {
                id: "fetch",
                src: "httpMachine",
                input: ({
                    url: fetchPrincipalUrl,
                    method: 'get',
                    isChildMachine: true,
                    unauthorisedAutoRedirect: false
                }),
                onDone: [
                    {
                        guard: ({ event }) => event.output?.responseEnvelope?.data?.identity?.clientAccount !== null,
                        actions: [
                            assign({
                                principals: ({ event }) => principalMapper(event.output!.responseEnvelope!.data),
                                errorMessage: () => undefined
                            }),
                            ({ context }) => {
                                //console.debug(`PrincipalProviderMachineStates.Fetching: onDone: principals = `, context.principals);
                                datadogLogs.setGlobalContextProperty('principal', context.principals?.primaryPrincipal)
                            }
                        ],
                        target: PrincipalProviderMachineStates.Fetched,
                    },
                    {
                        target: PrincipalProviderMachineStates.Fetched,
                        actions: [
                            assign({
                                principals: () => new PrincipalsCollection(),
                                errorMessage: () => undefined
                            })
                        ]
                    },
                ],
                onError: [
                    {
                        guard: ({ event }) => (event as any).error?.problemDetails?.status === 401,
                        target: PrincipalProviderMachineStates.Fetched,
                        actions: [
                            assign({
                                principals: () => new PrincipalsCollection(),
                                errorMessage: () => undefined
                            })
                        ]
                    },
                    {
                        target: PrincipalProviderMachineStates.FetchError,
                        actions: [
                            ({ event }) => {
                                console.error(`PrincipalProviderMachineStates.Fetching: onError: event = `, event);
                            },
                            assign({
                                principals: () => undefined,
                                errorMessage: ({ event }: any) => (event as any).error?.problemDetails?.title
                            })
                        ]
                    }
                ]
            }
        },
        [PrincipalProviderMachineStates.Fetched]: {
            entry: () => {
                closeLoadingScreen()
            },
            on: {
                [PrincipalProviderMachineEvents.Refetch]: PrincipalProviderMachineStates.Fetching
            }
        },
        [PrincipalProviderMachineStates.FetchError]: {
            entry: () => {
                closeLoadingScreen()
            }
        },
    }
});


export function principalMapper(source: any): PrincipalsCollection {
    //console.debug(`principalMapper: source = `, JSON.stringify(source));
    let principal = {
        identity: {
            isAuthenticated: source.identity.isAuthenticated === true,
            userId: source.identity.userId,
            username: source.identity.username,
            name: source.identity.name,
            clientAccount: source.identity.clientAccount !== null
                ? {
                    id: source.identity.clientAccount.id,
                    name: source.identity.clientAccount.name
                }
                : null
        },
        hasMultipleClientAccounts: source.hasMultipleClientAccounts,
        isGssAdministrator: source.isGssAdministrator === true,
        access: source.access as UserAccess,
        impersonator: source.impersonator
    } as IPrincipal;
    var principalsCollection = new PrincipalsCollection();

    principalsCollection.primaryPrincipal = { ...(new Principal()), ...principal } as IPrincipal;

    //console.debug(`principalMapper: principalsCollection = `, JSON.stringify(principalsCollection));
    return principalsCollection;
}

